import projImg1 from "../assets/img/Products03.jpg";
import projImg2 from "../assets/img/Products02.jpg";
import projImg3 from "../assets/img/Products01.jpg";
import projImg4 from "../assets/img/Products04.jpg";
import projImg5 from "../assets/img/Products05.jpg";
import projImg6 from "../assets/img/Products06.jpg";

import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { ArrowRightCircle } from "react-bootstrap-icons";

const pipelineBlocks = [
  {
    number: "01",
    name: "Demosaic",
    description:
      "Converts the Bayer-pattern sensor output into full-resolution RGB while preserving fine edges and colour borders.",
  },
  {
    number: "02",
    name: "Auto White Balance",
    description:
      "Monitors scene colour temperature and tweaks channel gains in real time so whites stay neutral and skin tones look natural.",
  },
  {
    number: "03",
    name: "Denoising",
    description:
      "Applies an adaptive spatial–temporal filter that knocks down low-light noise without smearing textures or edges.",
  },
  {
    number: "04",
    name: "Contrast & Edge Enhancement",
    description:
      "Lifts shadow detail and selectively sharpens high-frequency regions to make features pop.",
  },
  {
    number: "05",
    name: "Gamma Correction",
    description:
      "Maps the linear sensor signal to a perceptual curve, improving mid-tone contrast and shadow visibility for human viewers.",
  },
  {
    number: "06",
    name: "RGB → YCbCr Conversion",
    description:
      "Transforms the processed RGB stream into YCbCr format, enabling bandwidth-efficient transport and standards-compliant video encoding.",
  },
];

const productCores = [
  {
    id: "HDR",
    number: "02",
    category: "Dynamic Range Processing",
    title: "HDR IP Core",
    image: projImg3,
    alt: "HDR image processing IP core",
    text: "Combines multiple exposure frames in real time to create a single image with enhanced highlight and shadow detail. By fusing short- and long-exposure frames, it boosts a sensor’s native dynamic range from roughly 73 dB to about 96 dB, revealing features that would otherwise be clipped or lost in noise.",
    tags: ["Multi-Exposure Fusion", "Up to 96 dB", "Real-Time"],
  },
  {
    id: "IMR",
    number: "03",
    category: "Resolution Processing",
    title: "Image Resize IP Core",
    image: projImg6,
    alt: "Image resize processing IP core",
    text: "Performs real-time upscaling or downscaling, resampling incoming frames to any target resolution while preserving aspect ratio and edge sharpness. Ideal for matching sensor output to display formats or bandwidth-constrained links without external processing.",
    tags: ["Upscale / Downscale", "Aspect Ratio", "Edge Preservation"],
  },
  {
    id: "NonUni",
    number: "04",
    category: "Sensor Calibration",
    title: "Non Uniformity Correction IP Core",
    image: projImg5,
    alt: "Non uniformity correction IP core",
    text: "Calculates per-pixel gain–offset coefficients for precise radiometric calibration and applies them in real time to suppress spatial noise across the frame.",
    tags: ["Per-Pixel Calibration", "Gain & Offset", "Spatial Noise"],
  },
  {
    id: "TargetTrack",
    number: "05",
    category: "Vision Analytics",
    title: "Target Tracking IP Core",
    image: projImg4,
    alt: "Target tracking image processing IP core",
    text: "Locks onto a designated moving object in real time and maintains the lock through moderate occlusions or lighting changes. Each frame, the block outputs the target’s updated X- and Y-axis coordinates for low-latency guidance or analytics, while also overlaying a rectangular bounding box on the video stream to make the tracked object visually stand out.",
    tags: ["X / Y Coordinates", "Bounding Box", "Low Latency"],
  },
  {
    id: "imstabi",
    number: "06",
    category: "Motion Compensation",
    title: "Digital Image Stabilization IP Core",
    image: projImg1,
    alt: "Digital image stabilization IP core",
    text: "Extracts frame-to-frame correlation metrics directly from the video, estimates the direction and magnitude of shake, and shifts the region of interest in the opposite direction. The result is real-time vibration compensation that produces a steady, jitter-free stream—no external motion sensors required.",
    tags: ["Correlation-Based", "ROI Compensation", "Sensorless"],
  },
];

const productNavigation = [
  {
    id: "daylightV",
    number: "01",
    shortTitle: "Daylight Vision",
  },
  ...productCores.map((product) => ({
    id: product.id,
    number: product.number,
    shortTitle: product.title.replace(" IP Core", ""),
  })),
];

const ProductItems = () => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace("#", "");

    if (!hash) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }

    const scrollTimer = window.setTimeout(() => {
      const element = document.getElementById(hash);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);

    return () => window.clearTimeout(scrollTimer);
  }, [location]);

  return (
    <main className="product-cores-page">
      {/* HERO */}
      <section className="product-cores-hero">
        <div className="product-cores-hero__glow product-cores-hero__glow--one" />
        <div className="product-cores-hero__glow product-cores-hero__glow--two" />

        <div className="product-cores-container product-cores-hero__content">
          <span className="product-cores-eyebrow">
            FPGA-Based Vision Technology
          </span>

          <h1>
            Image Processing
            <span> IP Cores</span>
          </h1>

          <p>
            Hardware-accelerated image-processing building blocks developed for
            real-time vision pipelines, embedded imaging systems and
            FPGA-based camera applications.
          </p>

          <div className="product-cores-hero__stats">
            <div>
              <strong>06</strong>
              <span>Vision IP Cores</span>
            </div>

            <div>
              <strong>FPGA</strong>
              <span>Synthesizable Architecture</span>
            </div>

            <div>
              <strong>Real-Time</strong>
              <span>Video Processing</span>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK PRODUCT NAVIGATION */}
      <nav
        className="product-cores-index"
        aria-label="Image processing IP cores"
      >
        <div className="product-cores-container product-cores-index__inner">
          {productNavigation.map((product) => (
            <a key={product.id} href={`#${product.id}`}>
              <span>{product.number}</span>
              {product.shortTitle}
            </a>
          ))}
        </div>
      </nav>

      {/* DAYLIGHT VISION FEATURED SECTION */}
      <section
        id="daylightV"
        className="daylight-core product-core-scroll-target"
      >
        <div className="product-cores-container">
          <div className="daylight-core__header">
            <div>
              <span className="product-cores-eyebrow">
                Complete Processing Pipeline
              </span>

              <h2>Daylight Vision IP Core</h2>
            </div>

            <span className="daylight-core__number">01</span>
          </div>

          <div className="daylight-core__layout">
            <div className="daylight-core__visual">
              <img
                src={projImg2}
                alt="Daylight Vision real-time image processing pipeline"
              />

              <div className="daylight-core__visual-overlay">
                <span>Input</span>
                <div />
                <span>FPGA Pipeline</span>
                <div />
                <span>Output</span>
              </div>
            </div>

            <div className="daylight-core__content">
              <span className="daylight-core__label">
                Real-Time Image Processing Pipeline
              </span>

              <p className="daylight-core__intro">
                A real-time image-processing pipeline that turns raw sensor data
                into clean, colour-accurate video—fully synthesizable for FPGA.
              </p>

              <div className="daylight-core__pipeline">
                {pipelineBlocks.map((block) => (
                  <article
                    key={block.name}
                    className="daylight-pipeline-card"
                  >
                    <span>{block.number}</span>

                    <h3>{block.name}</h3>

                    <p>{block.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER IP CORES */}
      <section className="product-cores-list">
        <div className="product-cores-container">
          {productCores.map((product, index) => (
            <article
              id={product.id}
              key={product.id}
              className={`product-core-panel product-core-scroll-target ${
                index % 2 !== 0 ? "product-core-panel--reverse" : ""
              }`}
            >
              <div className="product-core-panel__visual">
                <img
                  src={product.image}
                  alt={product.alt}
                  loading="lazy"
                />

                <span className="product-core-panel__number">
                  {product.number}
                </span>

                <div className="product-core-panel__signal">
                  <span />
                  <span />
                  <span />
                </div>
              </div>

              <div className="product-core-panel__content">
                <span className="product-core-panel__category">
                  {product.category}
                </span>

                <h2>{product.title}</h2>

                <div className="product-core-panel__divider" />

                <p>{product.text}</p>

                <div className="product-core-panel__tags">
                  {product.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="product-cores-cta">
        <div className="product-cores-container">
          <div className="product-cores-cta__box">
            <div>
              <span className="product-cores-eyebrow">
                Custom FPGA Development
              </span>

              <h2>Need a vision core tailored to your system?</h2>

              <p>
                Contact our team to discuss sensor interfaces, processing
                requirements and FPGA integration for your imaging application.
              </p>
            </div>

            <div className="product-cores-cta__actions">
              <HashLink
                smooth
                to="/#connect"
                className="product-cores-button product-cores-button--light"
              >
                Contact Our Team
                <ArrowRightCircle />
              </HashLink>

              <Link
                to="/opal#opal"
                className="product-cores-button product-cores-button--outline"
              >
                Explore Opal-1920S
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductItems;